import { ChangeDetectorRef, Directive, Input, OnDestroy, OnInit } from '@angular/core'
import { ErrorAnchorService } from 'src/app/services/error-anchor.service'
import { COPY_DETAILS, ErrorTier, PresentedError } from 'src/utils/presented-error'

/**
 * Tier 2 of the error system: anything submitted from a modal renders inside that modal, so
 * the button that caused the failure is still on screen.
 *
 * Put it on the dialog's own content and read the claimed error straight from the template —
 * no component code, and no change at the call site, since `handleArpioError` already funnels
 * the error and the registry routes it here:
 *
 * ```html
 * <div arpSurfaceErrorHost="launch-recovery" #dialogError="surfaceErrorHost">
 *   @if (dialogError.error) {
 *     <arp-error-block [error]="dialogError.error" (dismissed)="dialogError.clear()"></arp-error-block>
 *   }
 * </div>
 * ```
 *
 * `ModalDialogComponent` renders its template outlet inside `@if (displayed)`, so this
 * registers exactly while the dialog is visible and deregisters when it closes.
 */
@Directive({
  selector: '[arpSurfaceErrorHost]',
  exportAs: 'surfaceErrorHost',
  standalone: false,
})
export class SurfaceErrorHostDirective implements OnInit, OnDestroy {
  /** Distinguishes hosts in `ErrorAnchorService.registeredIds` when debugging. */
  @Input('arpSurfaceErrorHost') hostId = 'dialog'

  /**
   * When false the host declines everything, so an error still reaches the user as a toast
   * rather than landing in a dialog that cannot act on it.
   */
  @Input() errorHostEnabled = true

  /**
   * Action ids this dialog handles via `(actionInvoked)`. Anything else that needs a host to
   * carry it out is dropped before rendering, because a button that does nothing is worse
   * than no button. `href` links and Copy Details run inside `<arp-error-block>` itself, so
   * they never need listing.
   */
  @Input() errorHostActions: readonly string[] = []

  /** The claimed error, or null. Read from the template. */
  error: PresentedError | null = null

  /** The tiers this host answers for. A subclass narrows it to the surface it really is. */
  protected readonly acceptedTiers: readonly ErrorTier[] = ['surface']

  private deregister: (() => void) | null = null

  constructor(
    private readonly anchors: ErrorAnchorService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.deregister = this.anchors.register({
      id: this.hostId,
      accept: (presented) => this.claim(presented),
    })
  }

  ngOnDestroy(): void {
    this.deregister?.()
  }

  clear(): void {
    this.error = null
    this.cdr.markForCheck()
  }

  protected claim(presented: PresentedError): boolean {
    if (!this.errorHostEnabled || !this.acceptedTiers.includes(presented.tier)) return false

    // A newer failure replaces the one on screen: it is the response to what the user just
    // did, and the dialog only has room for one.
    this.error = this.withRunnableActions(presented)
    this.cdr.markForCheck()
    return true
  }

  /** Keeps only the actions that will actually do something when clicked. */
  private withRunnableActions(presented: PresentedError): PresentedError {
    const actions = presented.actions.filter(
      (action) => action.href || action.id === 'copy-details' || this.errorHostActions.includes(action.id)
    )
    if (actions.length === presented.actions.length) return presented
    // Copy Details is the floor: a host that can run none of the remedies still has to leave
    // the user something to do, or the block renders with no actions at all.
    return { ...presented, actions: actions.length ? actions : [COPY_DETAILS] }
  }
}
